import React, { useState, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, ArrowLeft, KeyRound, CheckCircle } from 'lucide-react';

const Login = () => {
  const { login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // Password reset flow state
  const [resetMode, setResetMode] = useState(false); // 'email' | 'code' | 'newPassword' | 'success' | false
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      login(response.data.token, response.data.user);
      toast.success('Bem-vindo de volta!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email ou palavra-passe incorretos');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      if (response.data.status === 'success') {
        toast.success('Código enviado para o seu email');
        setResetMode('code');
      } else {
        toast.error(response.data.message || 'Erro ao enviar código');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-reset-code`, { 
        email: resetEmail, 
        code: resetCode 
      });
      
      if (response.data.valid) {
        setResetToken(response.data.reset_token);
        toast.success('Código verificado!');
        setResetMode('newPassword');
      } else {
        toast.error(response.data.message || 'Código inválido');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('As palavras-passe não coincidem');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('A palavra-passe deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, { 
        email: resetEmail, 
        reset_token: resetToken,
        new_password: newPassword
      });
      
      if (response.data.status === 'success') {
        toast.success('Palavra-passe alterada com sucesso!');
        setResetMode('success');
      } else {
        toast.error(response.data.message || 'Erro ao alterar palavra-passe');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar palavra-passe');
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setResetMode(false);
    setResetEmail('');
    setResetCode('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Render password reset success
  if (resetMode === 'success') {
    return (
      <div className="login-container">
        <img
          src="https://images.unsplash.com/photo-1638368885492-7df8ff32920d?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Armazém"
          className="login-bg"
        />
        <div className="login-overlay" />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Palavra-passe Alterada!</h2>
              <p className="text-muted-foreground mb-6">A sua palavra-passe foi alterada com sucesso.</p>
              <Button
                onClick={resetToLogin}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Voltar ao Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render password reset flow
  if (resetMode) {
    return (
      <div className="login-container">
        <img
          src="https://images.unsplash.com/photo-1638368885492-7df8ff32920d?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Armazém"
          className="login-bg"
        />
        <div className="login-overlay" />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-8">
              <button
                type="button"
                onClick={resetMode === 'email' ? resetToLogin : () => setResetMode(resetMode === 'code' ? 'email' : 'code')}
                className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Voltar
              </button>
              
              <div className="flex items-center justify-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <KeyRound className="h-6 w-6 text-red-600" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-center mb-2">
                {resetMode === 'email' && 'Recuperar Palavra-passe'}
                {resetMode === 'code' && 'Introduzir Código'}
                {resetMode === 'newPassword' && 'Nova Palavra-passe'}
              </h2>
              <p className="text-sm text-muted-foreground text-center mb-6">
                {resetMode === 'email' && 'Introduza o email associado à sua conta'}
                {resetMode === 'code' && 'Introduza o código de 6 dígitos enviado para o seu email'}
                {resetMode === 'newPassword' && 'Defina a sua nova palavra-passe'}
              </p>

              {/* Step 1: Email */}
              {resetMode === 'email' && (
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="armazem@empresa.com"
                        className="pl-10"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        data-testid="reset-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-red-600 hover:bg-red-700"
                    disabled={loading}
                    data-testid="reset-send-code-btn"
                  >
                    {loading ? 'A enviar...' : 'Enviar Código'}
                  </Button>
                </form>
              )}

              {/* Step 2: Code verification */}
              {resetMode === 'code' && (
                <form onSubmit={handleVerifyCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-code">Código de Verificação</Label>
                    <Input
                      id="reset-code"
                      type="text"
                      placeholder="000000"
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                      maxLength={6}
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      data-testid="reset-code-input"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      Enviado para {resetEmail}
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-red-600 hover:bg-red-700"
                    disabled={loading || resetCode.length !== 6}
                    data-testid="reset-verify-code-btn"
                  >
                    {loading ? 'A verificar...' : 'Verificar Código'}
                  </Button>
                  <button
                    type="button"
                    onClick={handleRequestReset}
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                  >
                    Reenviar código
                  </button>
                </form>
              )}

              {/* Step 3: New password */}
              {resetMode === 'newPassword' && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nova Palavra-passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="reset-new-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmar Palavra-passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        data-testid="reset-confirm-password-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-red-600 hover:bg-red-700"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? 'A alterar...' : 'Alterar Palavra-passe'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render login form
  return (
    <div className="login-container">
      <img
        src="https://images.unsplash.com/photo-1638368885492-7df8ff32920d?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="Armazém"
        className="login-bg"
      />
      <div className="login-overlay" />
      
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-8">
            <div className="flex items-center justify-center mb-8">
              <img 
                src="/chaviarte-logo.png" 
                alt="Chaviarte" 
                className="h-20 object-contain"
              />
            </div>
            
            <h1 className="text-3xl font-bold text-center mb-2 tracking-tight">
              StockFlow
            </h1>
            <p className="text-sm text-muted-foreground text-center mb-8">
              Sistema de Gestão de Inventário
            </p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="armazem@empresa.com"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Palavra-passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                {loading ? 'Aguarde...' : 'Entrar'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setResetMode('email')}
                className="text-sm text-red-700 hover:underline"
                data-testid="forgot-password-link"
              >
                Esqueceu a palavra-passe?
              </button>
            </div>
          </div>
          
          <p className="text-center text-sm text-white/80 mt-6">
            Credenciais de demonstração: demo@warehouse.com / demo123
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
